/**
 * Video an eine Scroll-Position setzen ("Scrubben") — ohne Rueckstau.
 *
 * DAS PROBLEM (Jans Fund 04.08.2026: "das Video hakt ganz gewaltig — bewegt
 * sich erst sehr spaet und ruckelig beim Scrollen"):
 *
 * Der Scroll-Modus setzte `currentTime` in JEDEM Bild neu. Ein Sprung im Video
 * ist aber nicht sofort fertig — der Browser muss ab dem naechsten Keyframe
 * dekodieren. Trifft der naechste Sprungbefehl auf einen laufenden, staut sich
 * das auf: das Element ist praktisch dauerhaft im Sprung (nachgemessen: in 10
 * von 12 Proben `seeking === true`), und das angezeigte Bild arbeitet eine
 * Warteschlange ab, statt der Scrollposition zu folgen. Es wirkt traege und
 * ruckelig, obwohl die Rechnung stimmt.
 *
 * DIE REGEL HIER: immer nur EIN Sprung gleichzeitig. Waehrend einer laeuft,
 * wird das neue Ziel nur gemerkt. Ist das Element wieder frei, springt es auf
 * das ZULETZT gemerkte Ziel — also dorthin, wo der Nutzer inzwischen wirklich
 * ist, statt jede Zwischenstation abzuklappern. Zwischenziele zu ueberspringen
 * ist beim Scrubben genau richtig: gefragt ist das aktuelle Bild, nicht der Weg
 * dorthin.
 *
 * Der Aufrufer bekommt `true`, wenn wirklich gesprungen wurde, und weiss damit,
 * wann er die Textur neu hochladen muss.
 */

interface ScrubZustand {
    /** Zuletzt an das Element uebergebene Position. */
    scrubLetztesZiel?: number;
    /** Waehrend eines laufenden Sprungs gemerktes, neuestes Ziel. */
    scrubOffenesZiel?: number;
}

export interface ScrubbaresVideo extends ScrubZustand {
    videoPlayer: {
        currentTime: number;
        seeking?: boolean;
    };
}

/** Toleranz in Sekunden — darunter lohnt kein neuer Sprung. */
const TOLERANZ = 1e-3;

export function scrubVideoTo(video: ScrubbaresVideo | null | undefined, ziel: number): boolean {
    if (!video?.videoPlayer) return false;
    if (!Number.isFinite(ziel)) return false;

    // Neuestes Ziel immer merken — auch (gerade!) waehrend eines Sprungs.
    video.scrubOffenesZiel = ziel;

    // Laeuft noch ein Sprung, nichts weiter tun. Das naechste Bild der
    // Render-Schleife versucht es erneut; bis dahin ist das gemerkte Ziel
    // vielleicht schon wieder ein neueres.
    if (video.videoPlayer.seeking) return false;

    const gewuenscht = video.scrubOffenesZiel;
    const zuletzt = video.scrubLetztesZiel;
    if (zuletzt !== undefined && Math.abs(zuletzt - gewuenscht) <= TOLERANZ) {
        // Schon dort — kein erneutes Dekodieren, kein erneuter Textur-Upload.
        // Ein stehendes Video darf pro Bild NULL Arbeit machen.
        return false;
    }

    video.videoPlayer.currentTime = gewuenscht;
    video.scrubLetztesZiel = gewuenscht;
    return true;
}

/** Merkposten loeschen (z. B. beim Verlassen der Lebenszeit). */
export function resetScrub(video: ScrubbaresVideo | null | undefined): void {
    if (!video) return;
    video.scrubLetztesZiel = undefined;
    video.scrubOffenesZiel = undefined;
}
